import { useCallback, useState, useMemo, useRef } from 'react';
import type { DragContext } from '@model-framework/ui';
import { DraggableSVGGElement } from '@model-framework/ui';
import type { Position } from '@view-model/models/common/types/ui';
import { Point } from '@view-model/models/common/basic';
import { D3Mouse } from '@view-model/models/common/d3/D3Mouse';
import { useD3RightClickCallback } from '../../hooks/useD3RightClickCallback';

type Props = {
  position: Position;
  onDragStart?: (e: React.MouseEvent<SVGGElement>) => void;
  onDrag(context: DragContext): void;
  onDragEnd?: (context: DragContext) => void;
  onDragMoveStarted?: (e: React.MouseEvent<SVGGElement>) => void;
  onDragMoveEnded?: (context: DragContext) => void;
  onClick?: (e: React.MouseEvent<SVGGElement>) => void;
  onRightClick?: (position: Position, e: React.MouseEvent<SVGGElement>) => void;
  dragHandleContent: React.ReactElement | null;
  hoveringDragHandleContent?: React.ReactElement | null;
  children: React.ReactNode;
};

/**
 * ドラッグ移動可能な要素のうち、ドラッグハンドル部分を指定するようなコンテナ。
 *
 * ノードのように、対象要素のどこでも掴んで移動可能なものではなく、
 * 対象要素の特定部分のみを掴めるようなものを実現するためのコンテナコンポーネント。
 *
 * @param position 要素の表示位置
 * @param onDragStart
 * @param onDrag
 * @param onDragEnd
 * @param onDragMoveEnded
 * @param onDragMoveStarted
 * @param onClick
 * @param onRightClick
 * @param dragHandleContent ドラッグハンドル要素
 * @param hoveringDragHandleContent コンテンツ要素上をホバーしているときに表示するドラッグハンドル
 * @param children コンテンツ要素
 * @constructor
 */
export const DraggableContainer: React.FC<Props> = ({
  position,
  onDragStart,
  onDrag,
  onDragEnd,
  onDragMoveEnded,
  onDragMoveStarted,
  onClick,
  onRightClick,
  dragHandleContent,
  hoveringDragHandleContent,
  children,
}: Props) => {
  const [hovering, setHovering] = useState(false);
  const handleMouseEnter = useCallback(() => setHovering(true), []);
  const handleMouseLeave = useCallback(() => setHovering(false), []);
  const myRef = useRef<SVGGElement>(null);
  const d3Mouse = useMemo(() => new D3Mouse(myRef), []);

  useD3RightClickCallback(
    myRef,
    useCallback(
      (event: MouseEvent) => {
        const [x, y] = d3Mouse.getPosition(event);
        if (onRightClick) onRightClick({ x, y }, event as unknown as React.MouseEvent<SVGGElement>);
      },
      [d3Mouse, onRightClick]
    ),
    // 本来はハンドルする場合だけ伝搬を止めればいいが、現状ではズームが発動するのでstopPropagationで伝搬を止める
    true
  );

  return (
    <g ref={myRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={onClick}>
      <g transform={Point.fromPosition(position).toSVGTranslate()}>{children}</g>
      {/* ドラッグハンドル部が手前に描画されるよに、後から描画する */}
      <DraggableSVGGElement
        position={position}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        onDragMoveStarted={onDragMoveStarted}
        onDragMoveEnded={onDragMoveEnded}
      >
        {hovering && hoveringDragHandleContent}
        {dragHandleContent}
      </DraggableSVGGElement>
    </g>
  );
};
